import { useEffect, useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { AuthContext } from '../../context/auth-context';
import { alertActions } from '../../../store/alert';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ACCAPI from '../../../services/api/integrations/acc-api';
import Logger from '../../../utils/logger';

const FIELDTIME_NUMBER_PATTERN = /^\d{7}[A-Za-z]$/;

const PHASE = {
  WARNING: 'warning',
  SEARCHING: 'searching',
  RESULTS: 'results',
  FORMAT_ERROR: 'formatError',
  SEARCH_ERROR: 'searchError',
};

const ACC_AUTH_ERROR_MESSAGE =
  'The connection to Autodesk has expired. Please sign in again ("Autodesk Sign In" in the user menu), then try linking again.';

const LINK_ERROR_TITLES = {
  400: 'Invalid ACC Project ID',
  403: 'Project already synced',
  404: 'Project not found',
  409: 'ACC project already linked',
};

const describeError = (err, title) => {
  const data = err?.response?.data;

  if (err?.response?.status === 401 && Boolean(data?.authUrl)) {
    return {
      title: 'Autodesk connection expired',
      message: ACC_AUTH_ERROR_MESSAGE,
    };
  }

  if (!err?.response) {
    return {
      title,
      message:
        "Couldn't reach the server. Check your connection and try again.",
    };
  }

  return {
    title,
    message:
      data?.message ||
      (typeof data?.error === 'string' ? data.error : null) ||
      err.message,
  };
};

export const LinkAccProjectDialog = ({ open, project, onClose, onLinked }) => {
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const [phase, setPhase] = useState(PHASE.WARNING);
  const [jobNumber, setJobNumber] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [manualId, setManualId] = useState('');
  const [linking, setLinking] = useState(false);

  const projectNumber = String(project?.number ?? '').trim();
  const isRelink =
    !!project?.autodesk_project_id &&
    String(project.autodesk_project_id).trim() !== '';

  const jobNumberPreview = FIELDTIME_NUMBER_PATTERN.test(projectNumber)
    ? projectNumber.slice(0, 7)
    : projectNumber;

  const showError = (details) => dispatch(alertActions.showAlert(details));

  useEffect(() => {
    if (open) {
      setPhase(PHASE.WARNING);
      setJobNumber('');
      setMatches([]);
      setSelectedIndex(null);
      setManualId('');
      setLinking(false);
    }
  }, [open]);

  const runSearch = async () => {
    if (!FIELDTIME_NUMBER_PATTERN.test(projectNumber)) {
      setPhase(PHASE.FORMAT_ERROR);
      return;
    }

    setPhase(PHASE.SEARCHING);

    try {
      // projectId excludes this project from the already-linked lookup, so a
      // re-link doesn't report the project as conflicting with itself.
      const data = await ACCAPI.searchAccProjects(
        projectNumber,
        project.id,
        auth.token
      );
      setJobNumber(data.jobNumber);
      setMatches(data.matches || []);
      setSelectedIndex(data.matches?.length === 1 ? 0 : null);
      setPhase(PHASE.RESULTS);
    } catch (err) {
      Logger.error('ACC project search failed', err);
      setPhase(PHASE.SEARCH_ERROR);
      showError(describeError(err, "Couldn't search ACC"));
    }
  };

  const submitLink = async (autodeskProjectId) => {
    setLinking(true);

    try {
      const data = await ACCAPI.linkAccProject(
        project.id,
        autodeskProjectId,
        auth.token
      );
      Logger.info('Linked project to ACC', {
        projectId: project.id,
        autodeskProjectId,
        replaced: data.previousAutodeskProjectId,
        autoSync: data.autoSync,
      });
      // autoSync says whether the backend started the project's ACC sync.
      onLinked(data.project, data.autoSync);
      onClose();
    } catch (err) {
      Logger.error('ACC project link failed', err);
      setLinking(false);
      showError(
        describeError(
          err,
          LINK_ERROR_TITLES[err?.response?.status] || "Couldn't link project"
        )
      );
    }
  };

  const renderManualEntry = () => (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        size="small"
        label="ACC Project ID"
        value={manualId}
        onChange={(e) => setManualId(e.target.value)}
        disabled={linking}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.5 }}
      >
        Open the project in ACC — the ID is the UUID at the end of the URL, e.g.
        .../projects/0ebfe96c-7af5-43af-b8f4-5afaaebd5228
      </Typography>
    </Box>
  );

  const renderMatch = (match) => (
    <>
      <Typography variant="subtitle2">{match.name}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Job number {jobNumber}
      </Typography>
      {match.alreadyLinkedTo && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Already linked to Fieldtime project {match.alreadyLinkedTo.number} (
          {match.alreadyLinkedTo.name}). Unlink it there first.
        </Alert>
      )}
    </>
  );

  const selectedMatch =
    selectedIndex !== null && selectedIndex >= 0
      ? matches[selectedIndex]
      : null;
  const canConfirmMatch =
    !!selectedMatch && !selectedMatch.alreadyLinkedTo && !linking;
  const canSubmitManual = manualId.trim() !== '' && !linking;
  const showManualEntry =
    phase === PHASE.SEARCH_ERROR ||
    phase === PHASE.FORMAT_ERROR ||
    (phase === PHASE.RESULTS && matches.length === 0);

  const renderContent = () => {
    if (phase === PHASE.WARNING) {
      return (
        <Typography variant="body2">
          This will search ACC for a project whose name starts with the first 7
          digits of this project&apos;s number ({jobNumberPreview}). Make sure
          the corresponding ACC project&apos;s name actually begins with that
          number, or the search won&apos;t find a match.
          <br />
          <br />
          Once linked, Fieldtime syncs the project from ACC and sets it up for
          ACC asset sync: it creates a status set (mirroring this project&apos;s
          workflow) and custom fields in ACC. They cannot be removed from
          Fieldtime afterwards, and the link can no longer be changed here.
        </Typography>
      );
    }

    if (phase === PHASE.SEARCHING) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Searching ACC…</Typography>
        </Box>
      );
    }

    return (
      <>
        {phase === PHASE.FORMAT_ERROR && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Project number &quot;{projectNumber}&quot; can&apos;t be searched
            automatically — that needs 7 digits followed by a trade letter, e.g.
            1234567E. Enter the ACC Project ID directly instead.
          </Alert>
        )}

        {phase === PHASE.SEARCH_ERROR && (
          <Typography variant="body2">
            The ACC search couldn&apos;t be completed. You can enter the ACC
            Project ID directly instead.
          </Typography>
        )}

        {phase === PHASE.RESULTS && matches.length === 0 && (
          <Typography variant="body2">
            No matching ACC project found for job number {jobNumber}.
          </Typography>
        )}

        {phase === PHASE.RESULTS && matches.length === 1 && (
          <Box>{renderMatch(matches[0])}</Box>
        )}

        {phase === PHASE.RESULTS && matches.length > 1 && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {matches.length} ACC projects start with {jobNumber}. Select the
              correct one.
            </Typography>
            <List dense disablePadding>
              {matches.map((match, index) => (
                <ListItemButton
                  key={match.autodeskProjectId}
                  selected={selectedIndex === index}
                  disabled={!!match.alreadyLinkedTo || linking}
                  onClick={() => setSelectedIndex(index)}
                >
                  <ListItemText
                    primary={match.name}
                    secondary={
                      match.alreadyLinkedTo
                        ? `Already linked to ${match.alreadyLinkedTo.number}`
                        : 'Available'
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        )}

        {showManualEntry && renderManualEntry()}

        {isRelink && !showManualEntry && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This project is currently linked to ACC project{' '}
            {project.autodesk_project_id}. Linking will replace it.
          </Alert>
        )}
      </>
    );
  };

  const renderActions = () => {
    if (phase === PHASE.WARNING) {
      return (
        <>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="contained" onClick={runSearch}>
            Continue
          </Button>
        </>
      );
    }

    if (phase === PHASE.SEARCHING) {
      return (
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
      );
    }

    return (
      <>
        <Button type="button" onClick={onClose} disabled={linking}>
          Cancel
        </Button>
        {showManualEntry ? (
          <Button
            type="button"
            variant="contained"
            disabled={!canSubmitManual}
            onClick={() => submitLink(manualId.trim())}
          >
            Link
          </Button>
        ) : (
          <Button
            type="button"
            variant="contained"
            disabled={!canConfirmMatch}
            onClick={() => submitLink(selectedMatch.autodeskProjectId)}
          >
            {matches.length > 1 ? 'Link Selected' : 'Confirm Link'}
          </Button>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isRelink ? 'Re-link ACC' : 'Link ACC'}</DialogTitle>
      <Divider />
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
};

export default LinkAccProjectDialog;
